import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import AuctionCountdown from '@/components/AuctionCountdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Gavel, DollarSign, Users, Award, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuctionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAuction();
  }, []);

  const fetchAuction = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auctions/current`);
      setAuction(data);
      if (data) {
        setBidAmount(String(Math.ceil(data.highest_bid + 50)));
      }
    } catch (error) {
      console.error('Error fetching auction:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceBid = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= auction.highest_bid) {
      toast.error(`Bid must be higher than $${auction.highest_bid.toLocaleString()}`);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/checkout/create-session`,
        {
          auction_id: auction.id,
          bid_amount: amount,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create checkout session');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-[#C5A059] font-serif text-2xl">Loading auction...</div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="min-h-screen pt-20 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto py-24 text-center">
          <Gavel size={64} className="mx-auto text-[#525252] mb-8" strokeWidth={1} />
          <h1 className="font-serif text-4xl text-[#F5F5F0] mb-4">No Active Auction</h1>
          <p className="text-[#A3A3A3]">
            Check back soon for our next monthly auction featuring original artworks by Akanni.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition pt-20">
      {/* Header */}
      <section className="py-16 px-6 lg:px-12 max-w-7xl mx-auto">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">
          Monthly Auction
        </p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F5F5F0] tracking-tighter leading-none">
          {auction.title}
        </h1>
      </section>

      {/* Auction Content */}
      <section className="pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Artwork Image */}
          <div className="lg:col-span-7">
            <div className="artwork-container overflow-hidden sticky top-24">
              <img
                src={auction.artwork?.image_url}
                alt={auction.artwork?.title}
                className="artwork-image w-full h-auto max-h-[80vh] object-cover"
              />
            </div>
          </div>

          {/* Auction Details */}
          <div className="lg:col-span-5">
            {/* Countdown */}
            <div className="mb-8 p-6 bg-[#121212] border border-white/10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-4">
                Auction Ends In
              </p>
              <AuctionCountdown endDate={auction.end_date} />
            </div>

            {/* Current Bid */}
            <div className="mb-8 p-6 bg-[#121212] border border-white/10 bid-pulse">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252]">
                  Current Highest Bid
                </p>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Users size={14} />
                  <span className="font-mono text-xs">{auction.bid_count} bids</span>
                </div>
              </div>
              <p className="font-serif text-5xl text-[#C5A059]">
                ${auction.highest_bid?.toLocaleString()}
              </p>
            </div>

            {/* Place Bid Button */}
            <button
              onClick={() => setBidModalOpen(true)}
              className="w-full btn-primary flex items-center justify-center gap-3 bg-[#C5A059] text-[#0A0A0A] px-8 py-5 font-mono text-sm uppercase tracking-[0.2em] hover:bg-[#E0BE77] transition-colors mb-8"
              data-testid="place-bid-button"
            >
              <Gavel size={20} />
              Place Your Bid
            </button>

            {/* Auction Info */}
            <div className="p-6 border border-white/10 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <AlertCircle size={20} className="text-[#C5A059] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-2">
                    Auction Fee
                  </p>
                  <p className="text-[#A3A3A3] text-sm">
                    A <span className="text-[#F5F5F0] font-semibold">$20 non-refundable fee</span> is required 
                    to place a bid. This helps ensure serious bidders only.
                  </p>
                </div>
              </div>
            </div>

            {/* Winner Benefits */}
            <div className="space-y-4">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252]">
                Winner Receives
              </p>
              <div className="flex items-start gap-4 p-4 border border-white/10">
                <Award size={24} className="text-[#C5A059] flex-shrink-0" />
                <div>
                  <p className="text-[#F5F5F0] font-medium mb-1">Original Artwork</p>
                  <p className="text-[#A3A3A3] text-sm">
                    The winning bidder receives the original artwork, signed by Akanni.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 border border-white/10">
                <Users size={24} className="text-[#C5A059] flex-shrink-0" />
                <div>
                  <p className="text-[#F5F5F0] font-medium mb-1">Meet & Greet</p>
                  <p className="text-[#A3A3A3] text-sm">
                    A personal meeting with artist Akanni to discuss the artwork and creative process.
                  </p>
                </div>
              </div>
            </div>

            {/* Artwork Details */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <h3 className="font-serif text-2xl text-[#F5F5F0] mb-4">
                {auction.artwork?.title}
              </h3>
              <p className="text-[#A3A3A3] leading-relaxed mb-6">
                {auction.artwork?.description}
              </p>
              <div className="space-y-3">
                {auction.artwork?.medium && (
                  <div className="flex justify-between">
                    <span className="font-mono text-xs uppercase tracking-wider text-[#525252]">
                      Medium
                    </span>
                    <span className="text-[#F5F5F0] text-sm">{auction.artwork.medium}</span>
                  </div>
                )}
                {auction.artwork?.dimensions && (
                  <div className="flex justify-between">
                    <span className="font-mono text-xs uppercase tracking-wider text-[#525252]">
                      Dimensions
                    </span>
                    <span className="text-[#F5F5F0] text-sm">{auction.artwork.dimensions}</span>
                  </div>
                )}
                {auction.artwork?.year && (
                  <div className="flex justify-between">
                    <span className="font-mono text-xs uppercase tracking-wider text-[#525252]">
                      Year
                    </span>
                    <span className="text-[#F5F5F0] text-sm">{auction.artwork.year}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Bids */}
            {auction.recent_bids && auction.recent_bids.length > 0 && (
              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-4">
                  Recent Bids
                </p>
                <div className="space-y-3">
                  {auction.recent_bids.map((bid, index) => (
                    <div
                      key={bid.id || index}
                      className="flex justify-between items-center py-2 border-b border-white/5"
                    >
                      <span className="text-[#A3A3A3] text-sm">
                        {bid.user_name || 'Anonymous'}
                      </span>
                      <span className="text-[#C5A059] font-mono">
                        ${bid.amount?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bid Modal */}
      <Dialog open={bidModalOpen} onOpenChange={setBidModalOpen}>
        <DialogContent className="bg-[#121212] border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#F5F5F0]">
              Place Your Bid
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              A $20 fee is required to place a bid. This fee is non-refundable.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {!user && (
              <div className="p-4 bg-[#1a1a1a] border border-[#C5A059]/30 rounded">
                <p className="text-[#C5A059] text-sm">
                  Please sign in to place a bid.
                </p>
              </div>
            )}

            <div>
              <label className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] block mb-2">
                Your Bid Amount (USD)
              </label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
                <Input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="pl-10 bg-transparent border-white/20 text-[#F5F5F0] focus:border-[#C5A059]"
                  placeholder={`Minimum: $${(auction.highest_bid + 1).toLocaleString()}`}
                  min={auction.highest_bid + 1}
                  data-testid="bid-amount-input"
                />
              </div>
              <p className="text-[#525252] text-xs mt-2">
                Current highest bid: ${auction.highest_bid?.toLocaleString()}
              </p>
            </div>

            <div className="p-4 bg-[#1a1a1a] border border-white/10">
              <div className="flex justify-between mb-2">
                <span className="text-[#A3A3A3] text-sm">Bid Amount</span>
                <span className="text-[#F5F5F0]">${Number(bidAmount || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-[#A3A3A3] text-sm">Auction Fee</span>
                <span className="text-[#F5F5F0]">$20.00</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/10">
                <span className="text-[#C5A059] font-medium">Pay Now</span>
                <span className="text-[#C5A059] font-medium">$20.00</span>
              </div>
            </div>

            <p className="text-[#525252] text-xs">
              You will only pay $20 to place this bid. If you win, you'll pay the winning bid amount separately.
            </p>

            <Button
              onClick={user ? handlePlaceBid : () => navigate('/login')}
              disabled={submitting}
              className="w-full bg-[#C5A059] text-[#0A0A0A] hover:bg-[#E0BE77] font-mono text-xs uppercase tracking-[0.2em] py-6"
              data-testid="confirm-bid-button"
            >
              {submitting ? 'Processing...' : user ? 'Pay $20 & Place Bid' : 'Sign In to Bid'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
